import React, { useState, useMemo } from 'react';
import { useUSTeamSalesRanking, USTeamSalesItem } from '@/hooks/useUSTeamSalesRanking';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Loader2, TrendingUp, DollarSign, Target, ArrowUpDown } from 'lucide-react';

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const YEARS = [2024, 2025, 2026];

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

type SortKey = 'totalSales' | 'totalReceived' | 'goalAmount' | 'goalProgress';
type SortDirection = 'asc' | 'desc';

export function USTeamDashboard() {
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [sortKey, setSortKey] = useState<SortKey>('totalSales');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const { data: ranking, isLoading, error } = useUSTeamSalesRanking({
    year: selectedYear,
    month: selectedMonth,
    businessUnit: 'us',
  });

  // Calculate aggregated metrics
  const metrics = useMemo(() => {
    if (!ranking?.length) {
      return { totalSold: 0, totalReceived: 0, teamGoal: 0, dealsCount: 0, goalProgress: 0 };
    }

    const totalSold = ranking.reduce((sum, r) => sum + r.totalSales, 0);
    const totalReceived = ranking.reduce((sum, r) => sum + r.totalReceived, 0);
    const teamGoal = ranking.reduce((sum, r) => sum + r.goalAmount, 0);
    const dealsCount = ranking.reduce((sum, r) => sum + r.dealsCount, 0);
    const goalProgress = teamGoal > 0 ? (totalSold / teamGoal) * 100 : 0;

    return { totalSold, totalReceived, teamGoal, dealsCount, goalProgress };
  }, [ranking]);

  // Sort ranking
  const sortedRanking = useMemo(() => {
    if (!ranking) return [];
    
    return [...ranking].sort((a, b) => {
      const aValue = a[sortKey];
      const bValue = b[sortKey];
      return sortDirection === 'desc' ? bValue - aValue : aValue - bValue;
    });
  }, [ranking, sortKey, sortDirection]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection(prev => prev === 'desc' ? 'asc' : 'desc');
    } else {
      setSortKey(key);
      setSortDirection('desc');
    }
  };

  const getStatusBadge = (progress: number) => {
    if (progress >= 100) {
      return <Badge variant="default" className="bg-primary/20 text-primary border-primary/30">Bateu ✓</Badge>;
    } else if (progress >= 70) {
      return <Badge variant="secondary" className="bg-secondary text-secondary-foreground">Quase</Badge>;
    } else {
      return <Badge variant="destructive" className="bg-destructive/20 text-destructive border-destructive/30">Atenção</Badge>;
    }
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <p className="text-destructive">Error loading data: {(error as Error).message}</p>
      </div>
    );
  }

  return (
    <div className="bg-background p-4 md:p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground">
            Performance Comercial - US
          </h1>
          <p className="text-muted-foreground mt-1">
            Vendas, recebimentos e metas do time
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Select 
            value={selectedMonth.toString()} 
            onValueChange={(v) => setSelectedMonth(Number(v))}
          >
            <SelectTrigger className="w-[130px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MONTHS.map(m => (
                <SelectItem key={m.value} value={m.value.toString()}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select 
            value={selectedYear.toString()} 
            onValueChange={(v) => setSelectedYear(Number(v))}
          >
            <SelectTrigger className="w-[100px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {YEARS.map(y => (
                <SelectItem key={y} value={y.toString()}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <>
          {/* Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-8">
            {/* Total Vendido */}
            <Card className="bg-card border-border">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Total Vendido
                </CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl md:text-3xl font-bold text-foreground">
                  {formatCurrency(metrics.totalSold)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {metrics.dealsCount} deals no período
                </p>
              </CardContent>
            </Card>

            {/* Total Recebido */}
            <Card className="bg-card border-border">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Total Recebido
                </CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl md:text-3xl font-bold text-foreground">
                  {formatCurrency(metrics.totalReceived)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {metrics.totalSold > 0 
                    ? `${Math.round((metrics.totalReceived / metrics.totalSold) * 100)}% do vendido`
                    : '0% do vendido'
                  }
                </p>
              </CardContent>
            </Card>

            {/* Meta do Time */}
            <Card className="bg-card border-border">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Meta do Time
                </CardTitle>
                <Target className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl md:text-3xl font-bold text-foreground">
                  {formatCurrency(metrics.teamGoal)}
                </div>
                <div className="mt-2">
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                    <span>{Math.round(metrics.goalProgress)}% atingido</span>
                  </div>
                  <Progress value={Math.min(metrics.goalProgress, 100)} className="h-2" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Consultants Table */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Vendedores</CardTitle>
            </CardHeader>
            <CardContent className="p-0 md:p-6 md:pt-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border hover:bg-transparent">
                      <TableHead className="text-muted-foreground">Vendedor</TableHead>
                      <TableHead 
                        className="text-muted-foreground cursor-pointer hover:text-foreground"
                        onClick={() => handleSort('totalSales')}
                      >
                        <div className="flex items-center gap-1">
                          Vendido
                          <ArrowUpDown className="h-3 w-3" />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="text-muted-foreground cursor-pointer hover:text-foreground"
                        onClick={() => handleSort('totalReceived')}
                      >
                        <div className="flex items-center gap-1">
                          Recebido
                          <ArrowUpDown className="h-3 w-3" />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="text-muted-foreground cursor-pointer hover:text-foreground"
                        onClick={() => handleSort('goalAmount')}
                      >
                        <div className="flex items-center gap-1">
                          Meta
                          <ArrowUpDown className="h-3 w-3" />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="text-muted-foreground cursor-pointer hover:text-foreground min-w-[180px]"
                        onClick={() => handleSort('goalProgress')}
                      >
                        <div className="flex items-center gap-1">
                          % Meta
                          <ArrowUpDown className="h-3 w-3" />
                        </div>
                      </TableHead>
                      <TableHead className="text-muted-foreground">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedRanking.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                          Nenhum dado encontrado para o período selecionado
                        </TableCell>
                      </TableRow>
                    ) : (
                      sortedRanking.map((item) => (
                        <TableRow key={item.userId} className="border-border">
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <Avatar className="h-8 w-8 md:h-10 md:w-10">
                                <AvatarImage src={item.avatarUrl || undefined} />
                                <AvatarFallback className="bg-primary/20 text-primary text-xs">
                                  {getInitials(item.userName)}
                                </AvatarFallback>
                              </Avatar>
                              <span className="font-medium text-foreground truncate max-w-[150px]">
                                {item.userName}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="font-medium text-foreground">
                            {formatCurrency(item.totalSales)}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {formatCurrency(item.totalReceived)}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {formatCurrency(item.goalAmount)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Progress 
                                value={Math.min(item.goalProgress, 100)} 
                                className="h-2 w-20 md:w-24" 
                              />
                              <span className="text-sm text-muted-foreground min-w-[40px]">
                                {Math.round(item.goalProgress)}%
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            {getStatusBadge(item.goalProgress)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
